import React, { useRef, useEffect, useState } from 'react';
import { FiCopy, FiEdit2, FiTrash2, FiMoreVertical, FiClipboard, FiTag, FiShare } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useMessages } from '../../context/MessagesContext';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { resolveDateTags } from '../../utils/dateCommandUtils';
import { getPriorityInfo, getAuthorText } from '../../utils/messageUtils';
import { toast } from '../../utils/customToast';
import { usePlanPermissions, getCurrentPlan } from '../../utils/planPermissions';
import useCodeBlockEnhancer, { highlightHtml } from '../../hooks/useCodeBlockEnhancer';

const apiBase = process.env.REACT_APP_API_URL || '/api';

const FullTextModal = ({ message, open, onClose, onCopy, copied = {}, onEdit, onDelete, onSort }) => {
    const closeRef = useRef(null);
    const copyRef = useRef(null);
    const contentRef = useRef(null);
    const { getAuthHeaders, user, activeTenantId } = useAuth();
    const currentUserId = user?._id || user?.id;
    const { getMessageById } = useMessages();

    // Injecter les boutons "Copier" sur les blocs de code après rendu
    useCodeBlockEnhancer(contentRef, [message?.content, open]);

    const [allMessages, setAllMessages] = useState([]);
    const [allTodos, setAllTodos] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [allUsers, setAllUsers] = useState([]);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    // Permissions pour les onglets personnalisés
    const currentPlan = getCurrentPlan(user, activeTenantId);
    const { canUseCustomTabs } = usePlanPermissions(currentPlan);

    // Close menu on outside click or Escape key
    useEffect(() => {
        const handleOutside = (e) => {
            if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };

        const handleKey = (e) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };

        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('keydown', handleKey);
        };
    }, [menuOpen]);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const [mRes, tRes, uRes] = await Promise.all([
                    fetch(`${apiBase}/messages`, {
                        headers: getAuthHeaders()
                    }),
                    fetch(`${apiBase}/todos`, {
                        headers: getAuthHeaders()
                    }),
                    fetch(`${apiBase}/tenants/${activeTenantId}/users`, {
                        headers: getAuthHeaders()
                    })
                ]);
                if (mRes.ok) setAllMessages(await mRes.json());
                if (tRes.ok) setAllTodos(await tRes.json());
                if (uRes.ok) setAllUsers(await uRes.json());
            } catch (e) {
                console.error('Error fetching data for mentions:', e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, getAuthHeaders, activeTenantId]);

    useEffect(() => {
        if (open && closeRef.current) closeRef.current.focus();
    }, [open]);

    if (!open || !message) return null;

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') onClose();
        if (e.key === 'Tab') {
            const focusable = [copyRef.current, closeRef.current].filter(Boolean);
            if (focusable.length < 2) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        }
    };

    const renderContentWithMentions = (htmlContent) => {
        let content = htmlContent || '';

        // Résolution dynamique des balises de dates {{date:Commande}}
        content = resolveDateTags(content);

        // Remplacer les mentions @user:id[Name]
        content = content.replace(/@user:([A-Za-z0-9_-]+)\[([^\]]+)\]/g, (m, id, name) => {
            const isMentioned = id === currentUserId;
            const mentionClass = isMentioned ? 'user-mention mentioned-user' : 'user-mention';
            return `<span class="${mentionClass}" data-user-id="${id}">${name}</span>`;
        });
        
        // Remplacer les mentions @todo:
        content = content.replace(/@todo:([A-Za-z0-9_-]+)/g, (m, id) => {
            const todo = allTodos?.find(t => t._id === id);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = sanitizeHtml(todo?.text || '');
            const label = tempDiv.textContent || tempDiv.innerText || `todo:${id}`;
            const truncated = label.length > 40 ? label.slice(0, 40) + '...' : label;
            return `<a href="app://todo/${id}" class="mention-link text-accent underline hover:opacity-80 font-semibold" data-mention-type="todo" data-mention-id="${id}">${truncated}</a>`;
        });
        
        // Remplacer les mentions @msg:
        content = content.replace(/@msg:([A-Za-z0-9_-]+)/g, (m, id) => {
            const msg = allMessages?.find(m => m._id === id);
            const label = msg?.title || `msg:${id}`;
            const truncated = label.length > 40 ? label.slice(0, 40) + '...' : label;
            return `<a href="app://msg/${id}" class="mention-link text-accent underline hover:opacity-80 font-semibold" data-mention-type="msg" data-mention-id="${id}">${truncated}</a>`;
        });
        
        return content;
    };

    const handleDeleteClick = () => {
        onDelete(message._id);
        onClose();
    };

    const handleDuplicateClick = () => {
        try {
            const dup = { ...message };
            // ensure no id so editor will treat as new
            if (dup._id) delete dup._id;
            dup.title = `Copie : ${message.title || ''}`;
            if (onEdit) onEdit(dup);
        } catch (e) {
            console.error('Error duplicating message', e);
        } finally {
            onClose();
        }
    };

    const handleContentClick = (e) => {
        const target = e.target;
        if (target.classList.contains('mention-link')) {
            e.preventDefault();
            e.stopPropagation();
            
            const mentionType = target.getAttribute('data-mention-type');
            const mentionId = target.getAttribute('data-mention-id');
            
            if (mentionType === 'todo') {
                onClose();
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('openTodo', { detail: { id: mentionId } }));
                }, 100);
            } else if (mentionType === 'msg') {
                const targetMessage = getMessageById(mentionId);
                if (targetMessage && onEdit) {
                    onClose();
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('openMsg', { detail: { id: mentionId } }));
                    }, 100);
                }
            }
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-2 sm:p-4"
            onKeyDown={handleKeyDown}
            tabIndex={-1}
        >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-[60rem] mx-auto flex flex-col max-h-[95vh] sm:max-h-[90vh]">
                <div className="flex items-center justify-between p-4 border-b dark:border-gray-700 flex-shrink-0">
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-black dark:text-white">{message.title}</h3>
                    </div>
                    <div className="flex gap-2 relative" ref={menuRef}>
                        <button
                            ref={copyRef}
                            onClick={() => onCopy(message._id, message.content || '')}
                            className="text-xs font-bold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition p-2 rounded flex items-center gap-1"
                            aria-label="Copier le texte"
                            title="Copier"
                        >
                            <FiClipboard className="h-4 w-4" aria-hidden="true" /> 
                            {copied[message._id] ? 'Copié' : ''}
                        </button>
                        <button
                            onClick={() => setMenuOpen(open => !open)}
                            className="text-xs font-bold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition p-2 rounded flex items-center"
                            aria-label="Plus d'options"
                            title="Plus d'options"
                        >
                            <FiMoreVertical className="h-4 w-4" />
                        </button>

                        {menuOpen && (
                            <div className="absolute right-0 top-full mt-2 mb-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border dark:border-gray-700 pb-1 z-50">
                                {onEdit && (
                                    <button
                                        onClick={() => { onEdit(message); setMenuOpen(false); onClose(); }}
                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition text-black dark:text-white"
                                    >
                                        <FiEdit2 className="h-4 w-4" /> Modifier
                                    </button>
                                )}

                                <button
                                    onClick={() => { handleDuplicateClick(); setMenuOpen(false); }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition text-black dark:text-white"
                                >
                                    <FiCopy className="h-4 w-4" /> Dupliquer
                                </button>

                                {canUseCustomTabs && onSort && (
                                    <button
                                        onClick={() => { onSort(new Set([message._id])); setMenuOpen(false); onClose(); }}
                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition text-black dark:text-white"
                                    >
                                        <FiTag className="h-4 w-4" /> Trier
                                    </button>
                                )}

                                <button
                                    onClick={() => {
                                        const shareUrl = `${window.location.origin}/messages?shareMsg=${message._id}`;
                                        navigator.clipboard.writeText(shareUrl);
                                        // Crée un input temporaire pour sélectionner le lien
                                        const tempInput = document.createElement('input');
                                        tempInput.value = shareUrl;
                                        document.body.appendChild(tempInput);
                                        tempInput.select();
                                        tempInput.setSelectionRange(0, 99999);
                                        setTimeout(() => {
                                            document.body.removeChild(tempInput);
                                        }, 200);
                                        toast.success('Lien copié dans le presse-papier !');
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition text-black dark:text-white"
                                    aria-label="Partager le message"
                                    title="Partager"
                                >
                                    <FiShare className="h-4 w-4" /> Partager
                                </button>

                                <div className="border-b dark:border-gray-700 px-3 hidden sm:block"></div>

                                {onDelete && (
                                    <button
                                        onClick={() => { handleDeleteClick(); setMenuOpen(false); }}
                                        className="w-full text-left font-medium px-4 py-2 text-sm text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition"
                                    >
                                        <FiTrash2 className="h-4 w-4" /> Supprimer
                                    </button>
                                )}
                            </div>
                        )}

                        <button
                            ref={closeRef}
                            onClick={onClose}
                            className="text-sm bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-bold py-2 px-3 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
                        >
                            Fermer
                        </button>
                    </div>
                </div>

                <div 
                    ref={contentRef}
                    className="p-4 sm:p-6 overflow-y-auto flex-1 prose prose-sm dark:prose-invert max-w-none text-black dark:text-gray-300 min-w-0"
                    style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    dangerouslySetInnerHTML={{ __html: highlightHtml(sanitizeHtml(renderContentWithMentions(message.content || ''))) }}
                    onClick={handleContentClick}
                />

                <div className="px-4 sm:px-6 pb-4 pt-2 border-t dark:border-gray-700 flex-shrink-0">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {getAuthorText(message)}
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {message.role} — {message.type}
                            </span>
                            <span 
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border ${getPriorityInfo(message.priority).bgColor} ${getPriorityInfo(message.priority).textColor} ${getPriorityInfo(message.priority).borderColor}`}
                                title={getPriorityInfo(message.priority).title}
                            >
                                <span className="text-sm font-bold">{getPriorityInfo(message.priority).icon}</span>
                                {getPriorityInfo(message.priority).label}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FullTextModal;